import { X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  COMPACT_VIEWPORT_QUERY,
  PHONE_VIEWPORT_QUERY,
  useMediaQuery,
} from "@/lib/responsive";
import { useBrowserBackDismiss } from "@/hooks/use-browser-back-dismiss";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  closeDisabled?: boolean;
  /** Present compact-screen workflows as a Sheet. */
  presentation?: "default" | "responsive-sheet";
  /** Choose whether responsive Sheets begin at phone or tablet widths. */
  responsiveSheetAt?: "phone" | "tablet";
  /**
   * Render the panel with no padding and no visible heading, for dialogs that
   * bring their own header — a full-bleed workspace where a map or list must
   * reach the panel edge. The title is still required and still names the
   * dialog for assistive technology; it is only hidden visually.
   */
  bare?: boolean;
}

/**
 * Compatibility surface for app dialogs, backed by the production shadcn
 * Radix primitive for modal semantics, focus trapping, and dismissal.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
  closeDisabled = false,
  presentation = "default",
  responsiveSheetAt = "phone",
  bare = false,
}: DialogProps) {
  const compactViewport = useMediaQuery(
    responsiveSheetAt === "tablet"
      ? COMPACT_VIEWPORT_QUERY
      : PHONE_VIEWPORT_QUERY
  );
  useBrowserBackDismiss({
    open,
    enabled: presentation === "responsive-sheet" && compactViewport,
    canDismiss: !closeDisabled,
    onDismiss: () => onOpenChange(false),
  });
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const rememberReturnFocus = () => {
    const activeElement = document.activeElement;
    returnFocusRef.current =
      activeElement instanceof HTMLElement ? activeElement : null;
  };
  const restoreReturnFocus = (event: Event) => {
    const returnTarget = returnFocusRef.current;
    returnFocusRef.current = null;
    if (!returnTarget?.isConnected) return;
    event.preventDefault();
    returnTarget.focus();
  };

  if (presentation === "responsive-sheet" && compactViewport) {
    return (
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && closeDisabled) return;
          onOpenChange(nextOpen);
        }}
      >
        <SheetContent
          side="right"
          aria-describedby={undefined}
          onOpenAutoFocus={rememberReturnFocus}
          onCloseAutoFocus={restoreReturnFocus}
          closeDisabled={closeDisabled}
          className={cn(
            "flex w-full flex-col gap-0 p-0 sm:max-w-lg max-lg:[&_button]:min-h-11 max-lg:[&_button]:min-w-11 max-lg:[&_input]:min-h-11 max-lg:[&_select]:min-h-11",
            className
          )}
        >
          <SheetHeader className="border-b px-6 py-4 pr-12 text-left">
            <SheetTitle>
              {title ?? <span className="sr-only">Dialog</span>}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && (
            <div className="shrink-0 border-t bg-background px-6 py-4">
              {footer}
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && closeDisabled) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={rememberReturnFocus}
          onCloseAutoFocus={restoreReturnFocus}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background shadow-xl outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            bare ? "overflow-hidden" : "overflow-y-auto p-4 sm:p-6",
            className
          )}
        >
          {bare ? (
            <DialogPrimitive.Title className="sr-only">
              {title ?? "Dialog"}
            </DialogPrimitive.Title>
          ) : (
            <div className="mb-4 flex min-h-11 items-start pr-10 sm:min-h-0">
              <DialogPrimitive.Title className="text-lg font-semibold leading-6 tracking-tight">
                {title ?? <span className="sr-only">Dialog</span>}
              </DialogPrimitive.Title>
            </div>
          )}
          <DialogPrimitive.Close asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-lg"
              className="absolute right-2 top-2 opacity-70 hover:opacity-100 md:h-8 md:w-8"
              aria-label="Close"
              disabled={closeDisabled}
            >
              <X />
            </Button>
          </DialogPrimitive.Close>
          {children}
          {footer && <div className="mt-4">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
